// FASE 05 — S4-B2/PO 2026-05-13: tela independente Tabelas de Precos (Materiais).
// Mockup: Fase5_TabelaPreço/29_Tabela_Preço_Materiais.
// Decisao D1.b: consome o backend legado via apiAntigo.
// Decisao D2.a: Tela 31 (CEM MOBI) fora deste escopo.
// Decisao D3.a: edicao inline com grid editavel (sera implementada no Bloco S4-B3).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, Add as AddIcon } from "@mui/icons-material";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import {
  TipoMaterialTP,
  TIPO_MATERIAL_TP_LABELS,
  TIPO_MATERIAL_TP_ENUM,
  ID_REFERENCIA_PADRAO_TABELA_PRECO,
  ITabelaPrecoListagem,
} from "./types/tabelaPreco.types";
import {
  listarTabelasPrecos,
  listarTiposLiberados,
  criarTabelaPreco,
  excluirTabelaPreco,
  localizarTabelaPorNome,
  tipoProdutoDoBackendParaTP,
} from "./services/tabelaPrecoService";
import { GridListagem } from "./components/GridListagem";
import { ModalNovaTabela } from "./components/ModalNovaTabela";
import { ModalConfirmacao } from "./Edicao/components/ModalConfirmacao";
import { useSomenteConsultaTabelaPreco } from "./hooks/useSomenteConsultaTabelaPreco";

const TIPOS_ORDEM: TipoMaterialTP[] = [
  "acabamentos",
  "componentes",
  "kits",
  "perfis",
  "superficies",
  "vidros",
];

const STORAGE_KEY_TIPO = "@MOF:tabelasPrecos:tipo";
const STORAGE_KEY_MODO = "@MOF:tabelasPrecos:modoEdicao";

// Enum do legado -> chave de aba. Espelho invertido de TIPO_MATERIAL_TP_ENUM.
const TIPO_MATERIAL_POR_ENUM: Record<number, TipoMaterialTP> = Object.entries(
  TIPO_MATERIAL_TP_ENUM
).reduce((acc, [tipo, valor]) => {
  acc[valor] = tipo as TipoMaterialTP;
  return acc;
}, {} as Record<number, TipoMaterialTP>);

// A listagem do legado devolve a situação já como texto ("Disponível para Mercado").
const ehDisponivelMercado = (situacao: string): boolean =>
  (situacao || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .includes("disponivel");

export const TabelasPrecos: React.FC = () => {
  const navigate = useNavigate();
  const { openNotification } = useNavigation();

  const [tipoAtivo, setTipoAtivo] = useState<TipoMaterialTP | null>(null);
  const [modoEdicao, setModoEdicao] = useState<boolean>(() => {
    return sessionStorage.getItem(STORAGE_KEY_MODO) === "true";
  });
  const [tabelas, setTabelas] = useState<ITabelaPrecoListagem[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [modalNovaAberto, setModalNovaAberto] = useState(false);
  const [idParaExcluir, setIdParaExcluir] = useState<number | null>(null);

  // FASE 05 — item 1/PO 2026-08-26: as abas passam a refletir os tipos liberados em
  // Gerência > Empresas > Tabela de Preços. Antes a lista era fixa com os 6 tipos.
  const [tiposLiberados, setTiposLiberados] = useState<TipoMaterialTP[]>([]);
  const [carregandoTipos, setCarregandoTipos] = useState(true);

  // FASE 05 — item 3/PO 2026-08-11: empresa em "apenas para consulta" nao ve
  // "Modo edição", "Adicionar nova tabela" nem as acoes do grid.
  const { somenteConsulta } = useSomenteConsultaTabelaPreco();
  const emModoEdicao = modoEdicao && !somenteConsulta;

  useEffect(() => {
    let ativo = true;

    listarTiposLiberados()
      .then((liberados) => {
        if (!ativo) return;

        const tipos = liberados
          .map((t) => TIPO_MATERIAL_POR_ENUM[t.idTipoMaterial])
          .filter((t): t is TipoMaterialTP => Boolean(t))
          .sort((a, b) => TIPOS_ORDEM.indexOf(a) - TIPOS_ORDEM.indexOf(b));

        setTiposLiberados(tipos);

        const salvo = sessionStorage.getItem(STORAGE_KEY_TIPO) as TipoMaterialTP | null;
        setTipoAtivo(salvo && tipos.includes(salvo) ? salvo : tipos[0] ?? null);
      })
      .finally(() => ativo && setCarregandoTipos(false));

    return () => {
      ativo = false;
    };
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const todas = await listarTabelasPrecos();
      setTabelas(todas);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const tabelasDoTipo = useMemo(() => {
    const doTipo = tabelas.filter((t) => tipoProdutoDoBackendParaTP(t.tipoProduto) === tipoAtivo);

    // FASE 05 — item 7/PO 2026-08-26: com o switch "Modo edição" desligado, a tela mostra
    // apenas as tabelas disponíveis para o mercado — é a versão que o cliente usa no CEM.
    if (emModoEdicao) return doTipo;

    // Reporte TP de 02/09 (item 1): editar uma tabela publicada NÃO pode fazê-la sumir daqui.
    // Tabela com versão publicada (lote DISPONIVEL) continua aparecendo como "Disponível
    // Mercado" — a linha abre a ConsultaPublicada, com os preços da última disponibilização —
    // enquanto a edição corre no switch ON. O CEM segue consumindo a publicada (decisão P4).
    return doTipo
      .filter((t) => ehDisponivelMercado(t.situacao) || t.possuiVersaoPublicada)
      .map((t) =>
        ehDisponivelMercado(t.situacao) ? t : { ...t, situacao: "Disponível para Mercado" }
      );
  }, [tabelas, tipoAtivo, emModoEdicao]);

  const handleTipoChange = (_e: React.SyntheticEvent, novo: TipoMaterialTP) => {
    setTipoAtivo(novo);
    sessionStorage.setItem(STORAGE_KEY_TIPO, novo);
  };

  const handleModoEdicaoChange = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setModoEdicao(checked);
    sessionStorage.setItem(STORAGE_KEY_MODO, String(checked));
  };

  const handleEditar = useCallback(
    (id: number) => {
      // FASE 05 — S4-B3: pagina de precificacao (deixou de ser stub no plano de 19/08).
      navigate(`/tabelas-precos/${id}/editar`);
    },
    [navigate]
  );

  // FASE 05 — item 7/PO 2026-08-26: com o switch desligado a ação da linha é consultar
  // os produtos e preços da versão publicada (só os que têm preço).
  const handleConsultar = useCallback(
    (id: number) => {
      navigate(`/tabelas-precos/${id}/editar?consulta=1`);
    },
    [navigate]
  );

  // FASE 05 — item 3/PO 2026-08-26: a confirmação era o diálogo nativo do browser
  // (window.confirm), fora da identidade visual do MOF. Agora usa o modal padrão.
  const handleConfirmarExclusao = useCallback(
    async (id: number) => {
      const ok = await excluirTabelaPreco(id);
      if (ok) {
        openNotification({ message: "Tabela de preços excluída com sucesso.", type: "success" });
        await carregar();
      } else {
        openNotification({ message: "Erro ao excluir tabela de preços.", type: "error" });
      }
    },
    [carregar, openNotification]
  );

  const handleConfirmarNova = async (dados: { nome: string }) => {
    if (!tipoAtivo) return;

    // FASE 05 — Feedback QA 2026-05-15: usa o mapping centralizado TIPO_MATERIAL_TP_ENUM
    // (alinhado com MaterialEnum.Tipo do legado — Kit = 6, nao 9 como no backend novo).
    const enumTipoMaterial = TIPO_MATERIAL_TP_ENUM[tipoAtivo];

    const resp = await criarTabelaPreco({
      nome: dados.nome,
      idTipoMaterial: enumTipoMaterial,
      // Decisao TP-1 do PO (19/08/2026): referencia fixa 36 (MERCADO).
      idReferencia: ID_REFERENCIA_PADRAO_TABELA_PRECO,
    });

    if (!resp.sucesso) {
      openNotification({ message: resp.mensagem || "Erro ao criar tabela.", type: "error" });
      return;
    }

    openNotification({ message: "Tabela criada com sucesso.", type: "success" });
    setModalNovaAberto(false);
    await carregar();

    // O POST do legado nao devolve o id da tabela criada; localizamos pelo nome + tipo
    // para abrir a pagina de precificacao, como o PO pediu no relatorio de 11/08.
    const id = resp.id ?? (await localizarTabelaPorNome(dados.nome, enumTipoMaterial));
    if (id) navigate(`/tabelas-precos/${id}/editar`);
  };

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <StackCustom
        sx={{
          backgroundColor: "#ECEDED !important",
          boxShadow: "none !important",
          border: "none !important",
        }}
      >
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: "#666" }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
              TABELAS DE PREÇOS
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            {/* FASE 05 — item 3/PO 2026-08-11: com a empresa liberada apenas para consulta,
                o proprio switch "Modo edição" some. */}
            {!somenteConsulta && (
              <FormControlLabel
                control={
                  <Switch checked={modoEdicao} onChange={handleModoEdicaoChange} />
                }
                label={
                  <Typography variant="body2" sx={{ color: "#0288D1", fontWeight: 500 }}>
                    Modo edição
                  </Typography>
                }
              />
            )}
            {/* FASE 05 — Feedback QA 2026-05-15: o botao deve ficar OCULTO (nao apenas desabilitado)
                enquanto o Modo Edicao estiver desligado. */}
            {emModoEdicao && (
              <Tooltip title="Adicionar nova tabela">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setModalNovaAberto(true)}
                  sx={{
                    backgroundColor: "#3C90AD",
                    "&:hover": { backgroundColor: "#2d6f87" },
                    textTransform: "uppercase",
                    fontWeight: 600,
                    borderRadius: 0,
                    boxShadow: "none",
                    px: 3,
                    py: 1.2,
                    fontSize: "0.875rem",
                  }}
                >
                  Adicionar nova tabela
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Breadcrumb */}
        <Box
          sx={{
            borderBottom: "2px solid #3C90AD",
            pb: 1.5,
            mb: 2,
            ml: 4.5,
          }}
        >
          <Breadcrumbs separator="›" sx={{ mt: 0 }}>
            <Link
              underline="hover"
              color="inherit"
              onClick={(e) => {
                e.preventDefault();
                navigate("/dashboard");
              }}
              sx={{ fontSize: "0.7rem", cursor: "pointer", textDecoration: "none" }}
            >
              Home
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.7rem" }}>
              Tabelas de Preços
            </Typography>
            <Typography color="text.primary" sx={{ fontSize: "0.7rem" }}>
              {tipoAtivo ? TIPO_MATERIAL_TP_LABELS[tipoAtivo] : ""}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* FASE 05 — item 1/PO 2026-08-26: sem nenhum tipo liberado, não há aba a exibir. */}
        {!carregandoTipos && tiposLiberados.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Nenhum tipo de produto está liberado para precificação nesta empresa. A liberação é
            feita em Gerência &gt; Empresas &gt; Tabela de Preços.
          </Alert>
        )}

        {/* Abas por tipo — só as liberadas para a empresa */}
        {tiposLiberados.length > 0 && tipoAtivo && (
        <Box sx={{ mb: 2, px: 0 }}>
          <Tabs
            value={tipoAtivo}
            onChange={handleTipoChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: "auto",
              "& .MuiTab-root": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                minHeight: 44,
                padding: "10px 24px",
                color: "#fff",
                backgroundColor: "#7ab8cc",
                borderRadius: 0,
                marginRight: "2px",
                "&.Mui-selected": { color: "#fff", backgroundColor: "#3C90AD" },
              },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {tiposLiberados.map((t) => (
              <Tab key={t} value={t} label={TIPO_MATERIAL_TP_LABELS[t]} />
            ))}
          </Tabs>
        </Box>
        )}

        {/* Grid */}
        <GridListagem
          tabelas={tabelasDoTipo}
          modoEdicao={emModoEdicao}
          carregando={carregando || carregandoTipos}
          onConsultar={handleConsultar}
          onEditar={handleEditar}
          onExcluir={(id) => setIdParaExcluir(id)}
        />
      </StackCustom>

      {tipoAtivo && (
        <ModalNovaTabela
          open={modalNovaAberto}
          tipo={tipoAtivo}
          onClose={() => setModalNovaAberto(false)}
          onConfirmar={handleConfirmarNova}
        />
      )}

      <ModalConfirmacao
        open={idParaExcluir !== null}
        titulo="Confirmar exclusão"
        mensagem="Confirma a exclusão desta tabela de preços? Esta ação não poderá ser desfeita."
        textoConfirmar="Excluir"
        onClose={() => setIdParaExcluir(null)}
        onConfirmar={async () => {
          const id = idParaExcluir;
          setIdParaExcluir(null);
          if (id !== null) await handleConfirmarExclusao(id);
        }}
      />
    </DashboardStackCustom>
  );
};

export default TabelasPrecos;
